import { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../PrescriptionList.css";
import { LocalizeSubMenu } from "../Localize";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "../ui/dropdown-menu";

import {
  Bell,
  Clock,
  Eye,
  FileCheck,
  FileText,
  HelpCircle,
  Info,
  LayoutDashboard,
  LogIn,
  LogOut,
  MessageCircle,
  Play,
  Save,
  Sparkles,
  SquarePen,
  Users,
} from "lucide-react";

import { useEffectOnce } from "../../hooks/useEffectOnce";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import useRefreshToken from "../../hooks/useRefreshToken";
import { config } from "../../Environment";
import { distributionService } from "../../services/distributionService";
import LoggerContext from "../../context/LoggerProvider";
import { WSContext } from "../../App";
import { LocationState, LocationObject } from "../../lib/types";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/drawer";
import ProcessChat from "../chat/processChat";
import { resolvePwfActivityLink } from "../../api/pwf";
import TakeawaysRunningActivity from "./chatPanel/takeaways";
import ActivityRunningPreview from "./activityRunningPreview";
import ActivityVisitingCard from "./ActivityVisitingCard";
import ActivityUsageGuide from "./ActivityUsageGuide";
import ChatIndicatorTab from "./ChatIndicatorTab";
import TakeawaysIndicatorTab from "./TakeawaysIndicatorTab";
import { useNotes } from "./chatPanel/takeaways/use-notes";
import { DndContext } from "@dnd-kit/core";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
// import moment from "moment";
import SaveAndContinueModal from "./modals/saveAndContinueModal";
import ActivityCompletionModal from "./modals/activityCompletionModal";
import WorkflowInfoDialog from "./modals/workflowInfo";
import ReminderManagementModal from "./modals/reminderManagement";
import { redirectOnLogout } from "../../lib/logout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { useBranding } from "../../branding/useBranding";
import TrialModeBanner from "./TrialModeBanner";
import { toast } from "sonner";
import { useSocket } from "../../hooks/useSocket";
import { isUserTester } from "../../lib/analytics";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import ExampleFollowerPane from "./exampleFollower/ExampleFollowerPane";
import EditableEngagementName from "./EditableEngagementName";
import { ReactComponent as ClaryNextLogo } from "../../images/Logo.svg";

const mockExamples = [
  {
    id: "example-1",
    title: "First Activity",
    description:
      "A complete walkthrough of deciding whether to launch a new product feature",
    lastUpdated: "2 days ago",
    isOutdated: false,
    currentStep: 3,
    totalSteps: 5,
  },
  {
    id: "example-2",
    title: "Second Activity",
    description: "Example of using this framework for organizational changes",
    lastUpdated: "1 week ago",
    isOutdated: true,
    currentStep: 2,
    totalSteps: 4,
  },
  {
    id: "example-3",
    title: "Third Activity",
    description: "How to apply decision criteria to financial planning",
    lastUpdated: "3 days ago",
    isOutdated: false,
    currentStep: 1,
    totalSteps: 5,
  },
];

const ExamplesDialog = ({
  showExamplesDialog,
  setShowExamplesDialog,
  lastViewedExample,
  setLastViewedExample,
}: any) => (
  <Dialog open={showExamplesDialog} onOpenChange={setShowExamplesDialog}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Example Engagements</DialogTitle>
        <DialogDescription>
          Review prepared examples to see how others have used this workflow
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {lastViewedExample && (
          <div className="p-3 rounded-lg bg-muted">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Continue Previous Example</h4>
              <Badge variant="secondary">
                <Clock className="w-3 h-3 mr-1" />
                Recently Viewed
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">
                {mockExamples.find((ex) => ex.id === lastViewedExample)?.title}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  // Fast-forward to current screen in the example
                  setShowExamplesDialog(false);
                  // Implementation would load the example and navigate to current step
                }}
              >
                <Play className="w-4 h-4 mr-1" />
                Continue
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-3">
          {mockExamples.map((example) => (
            <div
              key={example.id}
              className="p-4 transition-colors border rounded-lg hover:bg-muted/50"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{example.title}</h3>
                    {example.isOutdated && (
                      <Badge variant="destructive" className="text-xs">
                        Outdated
                      </Badge>
                    )}
                  </div>
                  <p className="mb-2 text-sm text-muted-foreground">
                    {example.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Updated {example.lastUpdated}</span>
                    <span>
                      {example.currentStep}/{example.totalSteps} steps
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLastViewedExample(example.id);
                      setShowExamplesDialog(false);
                      // Implementation would load example in review mode
                      // If current screen doesn't exist, start from beginning
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Review
                  </Button>
                </div>
              </div>

              {example.isOutdated && (
                <div className="p-2 mt-3 text-xs border border-yellow-200 rounded bg-yellow-50">
                  This example may be outdated due to workflow changes. Author
                  should review and update.
                </div>
              )}
            </div>
          ))}
        </div>

        {mockExamples.length === 0 && (
          <div className="py-8 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No examples available for this workflow</p>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const RunActivityPage = () => {
  const { auth, setAuth } = useAuth();
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const refresh = useRefreshToken();

  const location = useLocation();
  const navigate = useNavigate();

  // const [showExamplesDialog, setShowExamplesDialog] = useState(false);
  const [activityMetadata, setActivityMetadata] = useState<any>(null);
  const [showWorkflowInfoDialog, setShowWorkflowInfoDialog] = useState(false);
  // const [lastViewedExample, setLastViewedExample] = useState("");
  const [activityLastUpdatedDate, setActivityLastUpdatedDate] = useState("");
  const [prescriptionVersionNo, setPrescriptionVersionNo] = useState("");
  const [saveAndExitDestination, setSaveAndExitDestination] = useState("");
  const [isSaveAndExitModalOpen, setIsSaveAndExitModalOpen] = useState(false);
  const [isActivityCompletedModalOpen, setIsActivityCompletedModalOpen] =
    useState(false);
  const [isReminderManagementOpen, setIsReminderManagementOpen] =
    useState(false);
  const [processId, setProcessId] = useState("");
  const [engagementName, setEngagementName] = useState("");
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [trialProductId, setTrialProductId] = useState<string | undefined>(
    undefined
  );
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [showUsageGuide, setShowUsageGuide] = useState(false);
  const [pendingExample, setPendingExample] = useState<{
    processId: string;
    name: string;
    description: string;
  } | null>(null);
  const [isSavingExample, setIsSavingExample] = useState(false);

  // Example following state
  const [selectedExample, setSelectedExample] = useState<{
    _id: string;
    name: string;
    creatorName: string;
    logEntries: Array<{
      activity: string;
      activityDialog: any;
      userData: Record<string, any>;
      selectedOptionId?: string;
      prescriptionLanguage?: string;
      fiPrescriptionHistory?: string;
      parentProcessId?: string;
      isCurrentScreen?: boolean;
    }>;
    notes: Record<string, any>;
    prologueRecordings?: { audio?: any; video?: any; image?: any };
    epilogueRecordings?: { audio?: any; video?: any; image?: any };
    prologueImage?: any;
    epilogueImage?: any;
  } | null>(null);
  const [showExamplePane, setShowExamplePane] = useState(false);
  const [currentActivityForExample, setCurrentActivityForExample] =
    useState<string>("");
  const [isExampleDrawerOpen, setIsExampleDrawerOpen] = useState(false);

  // Ref to access current activity info from ActivityRunningPreview
  const currentActivityRef = useRef<{
    currentActivity: string;
    diagram: any;
    prescriptionLanguage: string;
  } | null>(null);

  const findWorkflowIdInQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("wid"); // null if not defined
  };
  const findWorkflowIdInStorage = () => {
    return sessionStorage.getItem("prescriptionID");
  };

  const workflowIdFromQuery = findWorkflowIdInQuery();
  const workflowIdFromStorage = findWorkflowIdInStorage();
  // If this is a PWF redirect (?source=pwf), don't initialize prescriptionId yet —
  // the useEffect below will navigate to ?wid=X once resolved, preventing a premature load attempt
  const isPwfSource = new URLSearchParams(location.search).get("source") === "pwf";
  const selectedPrescriptionId = isPwfSource ? null : (workflowIdFromQuery ?? workflowIdFromStorage);

  const [prescriptionId, setPrescriptionId] = useState(
    selectedPrescriptionId ?? ""
  );
  // True while we're waiting for the PWF → prescription redirect to complete
  const [resolvingPwf, setResolvingPwf] = useState(isPwfSource);

  // PWF Hybrid Bridge: if navigated via ?source=pwf&pwfId=X, resolve to a real prescriptionId
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get("source");
    const pwfId = params.get("pwfId");
    if (source !== "pwf" || !pwfId) return;

    resolvePwfActivityLink(retryFetch, pwfId)
      .then(({ prescriptionId: resolvedId }) => {
        if (resolvedId) {
          navigate(`/activities/run?wid=${resolvedId}`, { replace: true });
        } else {
          setResolvingPwf(false);
          toast.error("No linked activity found. Create one from the action item first.");
          navigate(-1);
        }
      })
      .catch(() => {
        setResolvingPwf(false);
        toast.error("Failed to load activity.");
        navigate(-1);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const saveChanges = useRef(true);
  const saveProgressRef = useRef<(() => Promise<void>) | null>(null);
  const hasChangesRef = useRef<(() => boolean) | null>(null);
  const navigateToFieldRef = useRef<((reference: any) => Promise<void>) | null>(
    null
  );

  // const [searchParams, setSearchParams] = useSearchParams();

  //   const [reference, setReference] = useState(null);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 800px)").matches
  );

  const handleContentScroll = useCallback((direction: string) => {
    // No longer used for first header visibility on mobile (it's always hidden).
    // Still passed down so RenderDialog can auto-collapse the expanded actions row.
  }, []);

  // State for pane reference
  const [paneReference, setPaneReference] = useState<{
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  } | null>(null);

  // State for chat sidebar/drawer
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatCollapsed, setIsChatCollapsed] = useState(true);

  // State for takeaways sidebar/drawer
  const [isTakeawaysCollapsed, setIsTakeawaysCollapsed] = useState(true);

  // State for visiting card position
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });

  // Handle quote pane functionality - used when user clicks quote button
  const handleQuotePane = (paneData: {
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  }) => {
    setPaneReference(paneData);
    setIsChatOpen(true); // Open the chat sidebar/drawer
    setIsChatCollapsed(false); // Ensure chat is visible

    // If a specific component is quoted, scroll to it
    if (paneData.componentKey) {
      setTimeout(() => {
        const element = document.querySelector(
          `[data-field-id="${paneData.componentKey}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          // Add a brief highlight effect
          element.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => {
            element.classList.remove("ring-2", "ring-primary", "ring-offset-2");
          }, 2000);
        }
      }, 100);
    }
  };

  // Callback to receive the navigation function from RenderDialog
  const handleSetNavigateToField = (
    navigationFn: (reference: any) => Promise<void>
  ) => {
    console.log("[RunActivity] Received navigation function from RenderDialog");
    navigateToFieldRef.current = navigationFn;
  };

  // Integrate useNotes hook for takeaways management (single source of truth)
  const {
    notes,
    loading: notesLoading,
    error: notesError,
    addNote,
    editNote,
    removeNote,
  } = useNotes({
    processId: auth?.email ? processId : undefined,
    onError: (error: any) => {
      toast.error(error);
    },
    onSuccess: (message: any) => {
      toast.success(message);
    },
  });

  // Handle adding a note/takeaway from field action menu
  const handleAddTakeaway = async (
    type: "action" | "insight",
    content: string,
    label?: string
  ) => {
    if (!content.trim()) {
      toast.error("Cannot add empty content to takeaways");
      return;
    }

    const success = await addNote({
      type,
      content: content.trim(),
      assignedTo: null,
      createdBy: "current-user",
    });

    if (success) {
      // Auto-open takeaways panel to show the new note
      setIsTakeawaysCollapsed(false);

      // Visual feedback
      const typeLabel = type === "action" ? "Action Item" : "Insight";
      toast.success(`Added to ${typeLabel}s`);
    }
  };

  // Handle selecting an example to follow
  const handleFollowExample = async (example: {
    _id: string;
    name: string;
    creator: { name: string };
    data: { userData: Record<string, any>; notes: Record<string, any> };
  }) => {
    try {
      // Fetch the example review data (uses same endpoint as Review.tsx)
      // This gives us the complete process_log history with all visits
      const response = await retryFetch(
        `${config.API_BASE}/api/review/process/${example._id}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch example data");
      }

      const reviewData = await response.json();
      const { processInfo, logEntries } = reviewData;

      // Filter out system markers and current screen entries
      // Keep only actual completed activities from the log
      const cleanedLogEntries = (logEntries || []).filter(
        (entry: any) =>
          !entry.activity?.startsWith("TERMINATE") &&
          !entry.activity?.startsWith("REOPEN|") &&
          !entry.activity?.startsWith("CURRENT_SCREEN|") &&
          !entry.isCurrentScreen // Exclude unsaved current screen
      );

      setSelectedExample({
        _id: example._id,
        name: example.name,
        creatorName: example.creator.name,
        logEntries: cleanedLogEntries,
        notes: processInfo.notes || {},
      });

      // Wait a tick to ensure currentActivityRef is populated
      setTimeout(() => {
        setShowExamplePane(true);
        // Auto-open drawer on mobile for discoverability
        if (isMobile) {
          setIsExampleDrawerOpen(true);
        }
      }, 100);
    } catch (error) {
      console.error("Failed to load example:", error);
      toast.error(t("failed_to_load_example") || "Failed to load example");
    }
  };

  // Handle stopping example following
  const handleStopFollowingExample = () => {
    setSelectedExample(null);
    setShowExamplePane(false);
    setIsExampleDrawerOpen(false); // Close mobile drawer
  };

  // Handle navigation to pane/field - used when clicking references in messages
  // Does NOT populate the chat input (no setPaneReference)
  // Uses the navigation handler provided by RenderDialog to navigate to previous steps if needed
  const handleNavigateToPane = async (paneData: {
    screen: string;
    screenLabel: string;
    component?: string;
    componentKey?: string;
  }) => {
    console.log("[RunActivity] handleNavigateToPane called:", paneData);
    // Use the navigation function from RenderDialog if available
    if (navigateToFieldRef.current) {
      console.log("[RunActivity] Using RenderDialog's navigation handler");
      await navigateToFieldRef.current(paneData);
    } else {
      // Fallback: just scroll and highlight (old behavior, only works if already on correct screen)
      console.warn(
        "[RunActivity] Navigation handler not available, using fallback"
      );
      if (paneData.componentKey) {
        setTimeout(() => {
          const element = document.querySelector(
            `[data-field-id="${paneData.componentKey}"]`
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            // Add a brief highlight effect
            element.classList.add("ring-2", "ring-primary", "ring-offset-2");
            setTimeout(() => {
              element.classList.remove(
                "ring-2",
                "ring-primary",
                "ring-offset-2"
              );
            }, 2000);
          } else {
            console.warn(
              `[RunActivity] Could not find element with data-field-id="${paneData.componentKey}"`
            );
          }
        }, 100);
      }
    }
  };

  const { Logger } = useContext(LoggerContext);

  const {
    socket,
    roomId,
    setRoomId,
    globalAbortController,
    listRefresh,
    setListRefresh,
  } = useContext(WSContext);

  /**
   * The `setSearchParams` function from `useSearchParams` causes a navigation,
   * so we use the `replaceState` function from the browser's History API instead.
   * @param patch object with key-value pairs to set or update in the query string
   */
  function updateSearchParams(patch: object) {
    /*
    setSearchParams((current) => {
      Object.entries(patch).forEach(([key, value]) => {
        current.set(key, value);
      });
      return current;
    });
    */
    const browserSearchParams = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      browserSearchParams.set(key, value);
    });
    window.history.replaceState({}, "", "?" + browserSearchParams.toString());
  }

  function getLocationState() {
    if (location.state && typeof location.state === "object") {
      return location.state as LocationState;
    }
    return null;
  }

  const state = getLocationState();

  const defaultFromLocation = { pathname: "/portal" };

  // the "from" location is where the user came from,
  // which we should also use as the destination when the
  // user exits the activity without completing it
  let from: LocationObject = state?.from ?? defaultFromLocation;

  // the "forward" location is set by the caller and specifies
  // where to send the user when they complete the activity.
  // if the user cancels or exits early without completing,
  // they would be redirected back to the "from" location
  let forward: LocationObject = state?.forward ?? from;

  // background shows that PREVIEW should be opened as modal on top of current page!
  let background: any | null = state?.background ?? { pathname: "/portal" };
  if (background?.pathname === "/") {
    background = null;
  }
  /*
  if (location.state && typeof location.state === "object") {
    const state = location.state as LocationState;
    if (state.background && typeof state.background === "object") {
      if ("pathname" in state.background) {
        background =
          state.background.pathname !== "/" ? state.background : null;
      }
    }
  }
  */

  const userInfo = useMemo(() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : null;
  }, []);

  const isAuthenticated = useMemo(() => {
    if (userInfo?.email) {
      return true;
    }
    return false;
  }, [userInfo]);

  async function loadPrescriptionMetadata(id: string) {
    if (!id) {
      return;
    }
    try {
      console.log(`fetching activity metadata for id: ${id}`);
      const activityMetadataResponse = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${id}/metadata?includeVendor=1`,
        {
          credentials: "include",
        }
      );

      if (!activityMetadataResponse.ok) {
        console.error(
          `Failed to fetch activity metadata for id: ${id}, status: ${activityMetadataResponse.status}`
        );
        // Handle malformed IDs (400/500) or missing activities (404)
        // Don't navigate - just skip setting metadata
        // ActivityRunningPreview will handle showing errors when starting the process
        return;
      }

      console.log(`got activity metadata for id: ${id}`);
      const activityMetadata = await activityMetadataResponse.json();
      console.log(
        `got activity metadata for id [${id}]: ${JSON.stringify(
          activityMetadata
        )}`
      );
      setActivityMetadata(activityMetadata);
      setActivityLastUpdatedDate(activityMetadata.lastUpdated);
    } catch (error) {
      console.error(
        `Error loading prescription metadata for id: ${id}:`,
        error
      );
      // Network or parsing errors - don't navigate, just log
      return;
    }
  }

  useEffectOnce(() => {
    const handler = (e: any) => setIsMobile(e.matches);
    window.matchMedia("(max-width: 800px)").addEventListener("change", handler);

    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("prescriptionHistoryID");
    sessionStorage.removeItem("runProcessID");

    // refreshLists();

    // subscribe to incoming messages
    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe((msg) => {
        Logger.debug("PrescriptionList incoming message: ");
        Logger.debug(msg);

        switch (msg.command) {
        }
      });

    return () => {
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (prescriptionId) {
      console.log(
        `useEffect prescriptionId ${prescriptionId} loadPrescriptionMetadata`
      );
      loadPrescriptionMetadata(prescriptionId);
      if (prescriptionId !== workflowIdFromQuery) {
        console.log(
          `useEffect updating search params because prescriptionId ${prescriptionId} !== workflowIdFromQuery ${workflowIdFromQuery}`
        );
        updateSearchParams({ wid: prescriptionId });
      }
    }
  }, [prescriptionId]);

  // Update page title when activity metadata loads
  useEffect(() => {
    if (activityMetadata?.name) {
      document.title = `${activityMetadata.name} | ClaryNext`;

      // Send page view to GA4 with updated title (using gtag with separate dataLayer)
      if (
        typeof window !== "undefined" &&
        typeof (window as any).gtag === "function"
      ) {
        const isTester = isUserTester();
        (window as any).gtag("event", "page_view", {
          page_title: `${activityMetadata.name} | ClaryNext`,
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
          is_tester: isTester,
          tester_mode: isTester ? "active" : "none",
        });
      }
    }
  }, [activityMetadata]);

  // Check if should show welcome screen (only once on initial load)
  const welcomeScreenChecked = useRef(false);
  useEffect(() => {
    // Only check once when we have both prescriptionId and activityMetadata
    if (prescriptionId && activityMetadata && !welcomeScreenChecked.current) {
      welcomeScreenChecked.current = true;
      const welcomeKey = `welcome_seen_${prescriptionId}`;
      const hasSeen = localStorage.getItem(welcomeKey) === "true";
      const hasProcessId = !!processId || location.search.includes("pid");

      // Show welcome screen if:
      // 1. No process ID (first time starting this activity)
      // 2. User hasn't seen welcome for this activity
      if (!hasProcessId && !hasSeen) {
        setShowWelcomeScreen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptionId, activityMetadata]);

  useEffect(() => {
    if (listRefresh) {
      // refreshLists(); // commented out because we don't need an updated activities list when running an already-selected activity
      setListRefresh(false);
    }
  }, [listRefresh]);

  const checkTrialMode = async () => {
    if (processId) {
      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/processes/${processId}`
        );

        // Don't navigate on errors - let ActivityRunningPreview handle error display
        // Just skip setting state if there's an error
        if (!response.ok) {
          console.error(
            "Failed to fetch process for trial mode check:",
            response.status
          );
          return;
        }

        const process = await response.json();

        // IMPORTANT: Only the initiator can access their process via /activities/run
        // Mentors/experts should use /review route instead
        if (auth?.email && process.fiInitiator?.email !== auth.email) {
          console.error(
            "Access denied: User is not the initiator of this process"
          );
          // Don't navigate - let ActivityRunningPreview handle this
          return;
        }

        if (process.trialMode) {
          setIsTrialMode(true);
          setTrialProductId(process.trialProductId);
        } else {
          setIsTrialMode(false);
          setTrialProductId(undefined);
        }
        // Set engagement name
        if (process.engagementName) {
          setEngagementName(process.engagementName);
        }
      } catch (error) {
        console.error("Failed to check trial mode:", error);
        // Don't navigate or show toast - let ActivityRunningPreview handle errors
        return;
      }
    }
  };

  useEffect(() => {
    // Check if the process is in trial mode
    checkTrialMode();
  }, [processId]);

  // Check for pending example metadata from Review page
  useEffect(() => {
    if (!processId) return;

    try {
      const pendingExampleData = sessionStorage.getItem("pendingExample");
      if (pendingExampleData) {
        const data = JSON.parse(pendingExampleData);
        // Only set if the processId matches
        if (data.processId === processId) {
          setPendingExample(data);
          Logger.info("[RunActivity] Detected pending example:", data);
        }
      }
    } catch (error) {
      Logger.error("[RunActivity] Error parsing pending example data:", error);
    }
  }, [processId, Logger]);

  // Cleanup pending example on unmount if user navigates away
  useEffect(() => {
    return () => {
      // If there's still a pending example when unmounting, clean it up
      // unless we're in the middle of saving
      if (pendingExample && !isSavingExample) {
        Logger.info("[RunActivity] Cleaning up pending example on unmount");
        sessionStorage.removeItem("pendingExample");
      }
    };
  }, [pendingExample, isSavingExample, Logger]);

  // Handle page visibility changes to refresh token when user returns after inactivity
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        // Save progress immediately when user is leaving the page
        // This ensures all changes are preserved when switching apps/tabs
        console.log("[RunActivity] Page hidden - saving progress");
        if (saveProgressRef.current) {
          try {
            await saveProgressRef.current();
            console.log("[RunActivity] Progress saved successfully");
          } catch (error) {
            console.warn("[RunActivity] Failed to save progress:", error);
          }
        }
      } else if (document.visibilityState === "visible") {
        // Refresh token when user returns to the page
        // Only refresh if the user is authenticated
        if (auth?.email && auth?.accessToken) {
          try {
            console.log("[RunActivity] Page visible - refreshing token");
            // Proactively refresh the token to ensure it's valid
            // This prevents errors when the user returns after the token has expired
            await refresh();
            console.log("[RunActivity] Token refreshed successfully");
          } catch (error) {
            console.warn("[RunActivity] Token refresh failed:", error);
            // If refresh fails (e.g., refresh token also expired), the user will need to log in again
            // The error will be handled by the useRefreshToken hook
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also listen for window focus/blur as a fallback for some mobile browsers
    const handleFocus = async () => {
      if (auth?.email && auth?.accessToken) {
        try {
          await refresh();
          console.log("[RunActivity] Token refreshed on focus");
        } catch (error) {
          console.warn("[RunActivity] Token refresh on focus failed:", error);
        }
      }
    };

    const handleBlur = async () => {
      console.log("[RunActivity] Window blur - saving progress");
      if (saveProgressRef.current) {
        try {
          await saveProgressRef.current();
          console.log("[RunActivity] Progress saved on blur");
        } catch (error) {
          console.warn("[RunActivity] Failed to save on blur:", error);
        }
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [auth?.email, auth?.accessToken, refresh, saveProgressRef]);

  const logout = async () => {
    // Close room first
    if (roomId && socket && socket.readyState === WebSocket.OPEN) {
      Logger.info("Message LEAVEROOM has been sent!");
      socket.send(
        JSON.stringify({
          command: "leaveroom",
          payload: undefined,
        })
      );
      socket.send(
        JSON.stringify({
          command: "changeuserstate",
          payload: "online",
        })
      );
      Logger.debug("HEADER: ROOM ID SET TO 0");
      setRoomId(0);
    }

    // abort with globalAbortController (mainly for loading prescription lists)
    sessionStorage.setItem("semaphore", "false");
    globalAbortController.abort();

    let response = await fetch(config.API_BASE + "/api/auth/logout", {
      credentials: "include",
    });
    Logger.debug(`logout response status ${response.status}`);

    if (!response.ok) alert(t("msg_error_logout"));

    if (response.status == 200) {
      try {
        let result = await response.json();
        Logger.debug(result);
      } catch (err) {
        Logger.debug(`failed to parse logout response`, err);
      }
    }
    setAuth({
      email: undefined,
      accessToken: undefined,
      impersonatedBy: undefined,
    });

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          command: "logout",
          payload: undefined,
        })
      );
    }

    Logger.debug(`logout in activity, savechanges is ${saveChanges.current}`);
    if (saveChanges.current) {
      navigate(redirectOnLogout({ location })); // return to this activity after sign-in
    } else {
      Logger.debug(
        `logout in activity, savechanges is ${saveChanges.current} returning to portal`
      );
      navigate(redirectOnLogout({ location: { pathname: "/portal" } }));
    }
  };

  const navigateToPortal = () => {
    navigate("/portal");
  };

  function computeLocationURL(loc: LocationObject) {
    if (loc.pathname && loc.search) {
      return `${loc.pathname}${loc.search}`;
    } else {
      return loc.pathname;
    }
  }

  const navigateToPriorLocation = () => {
    const dst = computeLocationURL(from);
    navigate(dst);
  };

  function beforeNavigatingToDashboard() {
    // Check if user has made any changes
    const hasChanges = hasChangesRef.current?.() ?? false;

    if (!hasChanges) {
      // No changes made, navigate directly to dashboard
      navigateToPortal();
    } else {
      // User has made changes, show save and exit modal
      setSaveAndExitDestination("portal");
      setIsSaveAndExitModalOpen(true);
    }
  }
  function beforeNavigatingToLogout() {
    // Check if user has made any changes
    const hasChanges = hasChangesRef.current?.() ?? false;

    if (!hasChanges) {
      // No changes made, logout directly
      logout();
    } else {
      // User has made changes, show save and exit modal
      setSaveAndExitDestination("logout");
      setIsSaveAndExitModalOpen(true);
    }
  }

  async function navigateToLogin() {
    // Save current engagement state before navigating to login
    if (saveProgressRef.current && processId) {
      try {
        await saveProgressRef.current();
      } catch (error) {
        console.error("Failed to save progress before login:", error);
      }
    }

    // Pass engagement transfer context if there's an active process
    const engagementTransfer = processId
      ? {
          processId,
          prescriptionId,
        }
      : undefined;

    navigate("/login", {
      state: {
        from: location,
        engagementTransfer,
      },
    });
  }

  function saveAndNavigateToPriorLocation() {
    // TODO: save any input on current page that wasn't saved yet
    navigateToPriorLocation();
  }

  function getDestinationAction(destinationKey: string) {
    if (destinationKey === "portal") {
      return navigateToPortal;
    }
    if (destinationKey === "logout") {
      return logout;
    }
    if (destinationKey == "prior_location") {
      return navigateToPriorLocation;
    }
    return null;
  }

  const handleStartActivity = () => {
    // Mark welcome as seen for this activity
    if (prescriptionId) {
      localStorage.setItem(`welcome_seen_${prescriptionId}`, "true");
    }
    setShowWelcomeScreen(false);
    // The ActivityRunningPreview will handle starting the process automatically
  };

  const handleSkipWelcome = () => {
    // Mark welcome as seen and hide it
    if (prescriptionId) {
      localStorage.setItem(`welcome_seen_${prescriptionId}`, "true");
    }
    setShowWelcomeScreen(false);
  };

  // Handle saving the copied process as an example
  const handleSaveAsExample = async () => {
    if (!pendingExample || !processId) return;

    setIsSavingExample(true);
    try {
      // Save any pending progress first
      if (saveProgressRef.current) {
        await saveProgressRef.current();
      }

      // Call the mark-as-example API
      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}/mark-as-example`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            exampleName: pendingExample.name,
            exampleDescription: pendingExample.description,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save as example");
      }

      const result = await response.json();
      Logger.info("[RunActivity] Process marked as example:", result);

      // Clear pending example from sessionStorage
      sessionStorage.removeItem("pendingExample");
      setPendingExample(null);

      toast.success(
        t("engagement_saved_as_example") ||
          "Engagement saved as example successfully!",
        { duration: 3000 }
      );

      // Navigate back to portal after a short delay
      setTimeout(() => {
        navigate("/portal");
      }, 1500);
    } catch (error: any) {
      Logger.error("[RunActivity] Error saving as example:", error);
      toast.error(
        error.message ||
          t("error_saving_example") ||
          "Failed to save as example"
      );
    } finally {
      setIsSavingExample(false);
    }
  };

  function renderProfileIconUsername() {
    return (
      <div className="flex items-center gap-2 p-2">
        {/* <Avatar className="w-8 h-8">
          <AvatarImage src="/placeholder.svg?height=32&width=32" />
          <AvatarFallback>JD</AvatarFallback>
        </Avatar> */}
        <div className="w-8 h-8 overflow-hidden rounded-full">
          <img
            src={`https://ui-avatars.com/api/?name=${
              userInfo?.email.split("@")[0]
            }`}
            alt="Avatar"
          />
        </div>
        <div>
          {/* <p className="text-sm font-medium">Umer Pall</p> */}
          {/* text-muted-foreground */}
          <p className="text-xs text-black">{userInfo?.email || "N/A"}</p>
        </div>
      </div>
    );
  }

  function renderLoginMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToLogin}>
        <LogIn className="w-4 h-4 mr-2" />
        <span>{t("login")}</span>
      </DropdownMenuItem>
    );
  }

  function renderSaveAndExitMenuItem() {
    return (
      <DropdownMenuItem onClick={saveAndNavigateToPriorLocation}>
        <Save className="w-4 h-4 mr-2" />
        <span>{t("link_save_and_exit")}</span>
      </DropdownMenuItem>
    );
  }

  function renderLogoutMenuItem() {
    return (
      <DropdownMenuItem onClick={beforeNavigatingToLogout}>
        <LogOut className="w-4 h-4 mr-2" />
        <span>{t("logout")}</span>
      </DropdownMenuItem>
    );
  }

  function renderDashboardMenuItem() {
    return (
      <DropdownMenuItem onClick={beforeNavigatingToDashboard}>
        <LayoutDashboard className="w-4 h-4 mr-2" />
        <span>{t("dashboard")}</span>
      </DropdownMenuItem>
    );
  }

  function renderAboutThisActivityMenuItem() {
    return (
      <DropdownMenuItem onClick={() => setShowWorkflowInfoDialog(true)}>
        <Info className="w-4 h-4 mr-2" />
        <span>{t("link_about_this_activity")}</span>
      </DropdownMenuItem>
    );
  }

  function renderMentoringMenuItem() {
    return (
      <DropdownMenuItem onClick={() => setIsChatCollapsed(false)}>
        <Users className="w-4 h-4 mr-2" />
        <span>{t("link_mentoring")}</span>
      </DropdownMenuItem>
    );
  }

  function renderManageReminderMenuItem() {
    return (
      <DropdownMenuItem onClick={() => setIsReminderManagementOpen(true)}>
        <Bell className="w-4 h-4 mr-2" />
        <span>{t("reminder_menu_item")}</span>
      </DropdownMenuItem>
    );
  }

  /*
  function renderShowExamplesItem() {
    return (
      <DropdownMenuItem onClick={() => setShowExamplesDialog(true)}>
        <Eye className="w-4 h-4 mr-2" />
        <span>Examples</span>
      </DropdownMenuItem>
    );
  }
  */

  function renderMenuItems() {
    if (isAuthenticated) {
      return (
        <>
          {renderProfileIconUsername()}
          <DropdownMenuSeparator />
          {renderAboutThisActivityMenuItem()}
          {!isTrialMode && renderMentoringMenuItem()}
          {!isTrialMode && renderManageReminderMenuItem()}
          {/* {!isTrialMode && renderShowExamplesItem()} */}
          {!isTrialMode && renderDashboardMenuItem()}
          {!isTrialMode && renderSaveAndExitMenuItem()}
          <LocalizeSubMenu />
          <DropdownMenuSeparator />
          {renderLogoutMenuItem()}
        </>
      );
    } else {
      return (
        <>
          {renderLoginMenuItem()}
          <DropdownMenuSeparator />
          {renderAboutThisActivityMenuItem()}
          <LocalizeSubMenu />
          {/* renderShowExamplesItem() */}
        </>
      );
    }
  }
  const { branding } = useBranding();

  // Mobile actions slot — rendered inside the expandable row of RenderDialog's green header
  // Order: Takeaways, Chat, Help, Menu (3-dot rightmost, includes Language)
  const mobileActionsSlot = isMobile ? (
    <>
      {/* Takeaways Drawer */}
      <Drawer>
        <DrawerTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-white hover:bg-white/20 active:scale-95"
          >
            <SquarePen className="w-5 h-5" />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="h-[80vh]">
          <TakeawaysRunningActivity
            processId={processId}
            isMobile={true}
            notes={notes}
            loading={notesLoading}
            error={notesError}
            addNote={addNote}
            editNote={editNote}
            removeNote={removeNote}
          />
        </DrawerContent>
      </Drawer>

      {/* Chat Sheet */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-white hover:bg-white/20 active:scale-95"
          >
            <MessageCircle className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent className="h-[80vh] p-0">
          <ProcessChat
            processId={processId}
            isMobile={true}
            paneReference={paneReference}
            onPaneReferenceUsed={() => {
              setPaneReference(null);
            }}
            onPaneReferenceClick={handleNavigateToPane}
          />
        </SheetContent>
      </Sheet>

      {/* Help / Quick Guide */}
      <Button
        variant="ghost"
        size="icon"
        className="w-8 h-8 text-white hover:bg-white/20 active:scale-95"
        onClick={() => setShowUsageGuide(true)}
        title={t("quick_guide")}
      >
        <HelpCircle className="w-5 h-5" />
      </Button>

      {/* Menu Dropdown — rightmost */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex flex-col justify-center items-center space-y-[3px] cursor-pointer w-8 h-8 rounded-md hover:bg-white/20 active:scale-95">
            <span className="w-[4px] h-[4px] bg-white rounded-full"></span>
            <span className="w-[4px] h-[4px] bg-white rounded-full"></span>
            <span className="w-[4px] h-[4px] bg-white rounded-full"></span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {renderMenuItems()}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  ) : null;

  // Socket.IO integration for real-time mentor events
  const socketHook = useSocket();

  // Listen for mentor:accepted event to auto-open chat
  useEffect(() => {
    if (!socketHook.isConnected || !processId) return;

    const socket = (window as any).socket;
    if (!socket) return;

    const handleMentorAccepted = (data: {
      processId: string;
      expertId: string;
      expertName: string;
    }) => {
      if (data.processId === processId) {
        setIsChatCollapsed(false); // Auto-expand chat
        toast.success(`${data.expertName} joined your session!`, {
          duration: 5000,
        });
      }
    };

    socket.on("mentor:accepted", handleMentorAccepted);

    return () => {
      socket.off("mentor:accepted", handleMentorAccepted);
    };
  }, [socketHook.isConnected, processId]);

  // Handle drag end to persist card position
  const handleDragEnd = (event: any) => {
    const { delta } = event;
    if (delta) {
      setCardPosition((prev) => ({
        x: prev.x + delta.x,
        y: prev.y + delta.y,
      }));
    }
  };

  // While resolving a PWF source redirect, render nothing — the useEffect above
  // will navigate to ?wid=X once the prescription ID is resolved
  if (resolvingPwf) {
    return null;
  }

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="relative flex flex-col w-full bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" style={{ height: "100dvh" }}>
        <div
          className="sticky top-0 z-20"
          style={{
            maxHeight: isMobile ? '0px' : undefined,
            overflow: isMobile ? 'hidden' : undefined,
            transition: isMobile ? 'max-height 0.3s ease' : undefined,
          }}
        >
          <div
            className="flex items-center justify-between h-10 md:h-14 px-3 py-1 md:py-2 bg-white/95 border-b border-gray-200/80 shadow-sm backdrop-blur-md md:px-4"
          >
          <div className="flex items-center flex-shrink-0">
            <ClaryNextLogo
                className="h-7 w-auto max-w-[120px] text-primary"
                style={{ height: "1.75rem", width: "auto" }}
            />
          </div>

          <div className="flex items-center flex-1 min-w-0 gap-1 ml-2 md:gap-2 md:ml-5">
            <div className="flex flex-col items-center flex-1 min-w-0">
              <h1 className="w-full text-sm font-medium text-center md:text-lg line-clamp-1">
                {activityMetadata?.name}
              </h1>
              {engagementName && (
                <EditableEngagementName
                  engagementName={engagementName}
                  processId={processId}
                  activityName={activityMetadata?.name}
                  onNameChange={setEngagementName}
                />
              )}
            </div>

            {/* Save as Example Button - Only show when editing a copy for example */}
            {pendingExample && (
              <Button
                variant="default"
                size="sm"
                className="flex-shrink-0 h-auto px-2 py-1 text-xs transition-all duration-200 bg-green-600 active:scale-95 hover:bg-green-700"
                onClick={handleSaveAsExample}
                disabled={isSavingExample}
                title={t("save_as_example")}
              >
                <FileCheck className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">
                  {isSavingExample
                    ? t("saving") || "Saving..."
                    : t("save_as_example") || "Save as Example"}
                </span>
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="flex-shrink-0 h-auto px-2 py-1 text-xs transition-all duration-200 text-primary hover:text-primary/80 hover:bg-primary/5 active:scale-95"
              onClick={() => setShowUsageGuide(true)}
              title={t("quick_guide")}
            >
              <HelpCircle className="w-4 h-4 md:mr-1" />
              <span className="hidden md:inline">{t("quick_guide")}</span>
            </Button>
          </div>

          <div className="flex items-center flex-shrink-0 gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex flex-col justify-center items-center space-y-[3.5px] cursor-pointer min-w-[36px] min-h-[36px] rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors duration-150">
                  <span className="w-[4.5px] h-[4.5px] bg-gray-600 rounded-full"></span>
                  <span className="w-[4.5px] h-[4.5px] bg-gray-600 rounded-full"></span>
                  <span className="w-[4.5px] h-[4.5px] bg-gray-600 rounded-full"></span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {renderMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        </div>
        {isTrialMode && (
          <TrialModeBanner
            productId={trialProductId}
            processId={processId}
            onPurchaseComplete={checkTrialMode}
          />
        )}

        {/* Pending Example Banner - Shows when editing a copy to save as example */}
        {pendingExample && (
          <div className="px-3 py-2 border-b border-primary/20 bg-primary/5 md:px-4">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <div className="flex items-center flex-1 min-w-0 gap-2">
                <FileCheck className="flex-shrink-0 w-4 h-4 text-primary" />
                <div className="flex flex-col min-w-0">
                  <p className="text-xs font-medium text-primary truncate md:text-sm">
                    {t("editing_copy_for_example") ||
                      "Editing copy to save as example"}
                  </p>
                  <p className="text-xs text-primary truncate">
                    {pendingExample.name}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0 h-auto px-2 py-1 ml-2 text-xs border-primary/30 hover:bg-primary/10"
                onClick={handleSaveAsExample}
                disabled={isSavingExample}
              >
                <FileCheck className="w-3 h-3 md:mr-1" />
                <span className="hidden md:inline">
                  {isSavingExample
                    ? t("saving") || "Saving..."
                    : t("save_example") || "Save"}
                </span>
              </Button>
            </div>
          </div>
        )}
        {/* Mobile Engagement Name Bar */}
        {isMobile && engagementName && (
          <EditableEngagementName
            engagementName={engagementName}
            processId={processId}
            activityName={activityMetadata?.name}
            onNameChange={setEngagementName}
            isMobile
          />
        )}

        {background &&
          (isMobile ? (
            <>
              <ActivityRunningPreview
                setActivityLastUpdatedDate={setActivityLastUpdatedDate}
                setPrescriptionVersionNo={setPrescriptionVersionNo}
                setIsActivityCompletedModalOpen={setIsActivityCompletedModalOpen}
                processId={processId}
                setProcessId={setProcessId}
                onQuotePane={handleQuotePane}
                onNavigateToField={handleSetNavigateToField}
                onAddTakeaway={handleAddTakeaway}
                saveProgressRef={saveProgressRef}
                hasChangesRef={hasChangesRef}
                activityMetadata={activityMetadata}
                currentActivityRef={currentActivityRef}
                setCurrentActivityForExample={setCurrentActivityForExample}
                onFollowExample={handleFollowExample}
                onContentScroll={handleContentScroll}
                mobileActionsSlot={mobileActionsSlot}
              />

              {/* Mobile Example Drawer */}
              {selectedExample && (
                <>
                  {/* Floating indicator when drawer is closed */}
                  {!isExampleDrawerOpen && (
                    <button
                      onClick={() => setIsExampleDrawerOpen(true)}
                      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all duration-200 active:scale-95"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t("following") || "Following"}: {selectedExample.name}
                      </span>
                    </button>
                  )}

                  {/* Example Drawer */}
                  <Drawer
                    open={isExampleDrawerOpen}
                    onOpenChange={setIsExampleDrawerOpen}
                  >
                    <DrawerContent className="h-[90vh]">
                      {currentActivityRef.current && (
                        <ExampleFollowerPane
                          diagram={currentActivityRef.current.diagram}
                          currentActivity={
                            currentActivityForExample ||
                            currentActivityRef.current.currentActivity
                          }
                          exampleLogEntries={selectedExample.logEntries}
                          exampleNotes={selectedExample.notes}
                          exampleName={selectedExample.name}
                          creatorName={selectedExample.creatorName}
                          onClose={handleStopFollowingExample}
                          processId={processId}
                          prescriptionLanguage={
                            currentActivityRef.current.prescriptionLanguage ||
                            "en"
                          }
                          prologueRecordings={{
                            ...(selectedExample.prologueRecordings || {}),
                            image: selectedExample.prologueImage,
                          }}
                          epilogueRecordings={{
                            ...(selectedExample.epilogueRecordings || {}),
                            image: selectedExample.epilogueImage,
                          }}
                        />
                      )}
                    </DrawerContent>
                  </Drawer>
                </>
              )}
            </>
          ) : (
            <PanelGroup direction="horizontal" className="w-full h-full">
              {/* Takeaways Panel - Left Side, Collapsible */}
              {!isTakeawaysCollapsed && (
                <>
                  <Panel
                    defaultSize={20}
                    minSize={15}
                    maxSize={35}
                    collapsible={true}
                    onCollapse={() => setIsTakeawaysCollapsed(true)}
                    onExpand={() => setIsTakeawaysCollapsed(false)}
                  >
                    <div className="h-full border-r bg-background">
                      <TakeawaysRunningActivity
                        processId={processId}
                        isMobile={false}
                        onCollapse={() => setIsTakeawaysCollapsed(true)}
                        notes={notes}
                        loading={notesLoading}
                        error={notesError}
                        addNote={addNote}
                        editNote={editNote}
                        removeNote={removeNote}
                      />
                    </div>
                  </Panel>
                  <PanelResizeHandle className="w-1 transition-all duration-200 bg-border hover:bg-primary/50 hover:w-1.5 shadow-sm" />
                </>
              )}

              {/* Activity Content Panel - Center */}
              <Panel minSize={30}>
                {showExamplePane &&
                selectedExample &&
                currentActivityRef.current?.currentActivity ? (
                  // Split view: User's pane + Example pane
                  <PanelGroup direction="horizontal" className="h-full">
                    {/* User's Working Pane */}
                    <Panel defaultSize={50} minSize={35}>
                      <div className="h-full overflow-auto">
                        <ActivityRunningPreview
                          setActivityLastUpdatedDate={
                            setActivityLastUpdatedDate
                          }
                          setPrescriptionVersionNo={setPrescriptionVersionNo}
                          setIsActivityCompletedModalOpen={
                            setIsActivityCompletedModalOpen
                          }
                          processId={processId}
                          setProcessId={setProcessId}
                          onQuotePane={handleQuotePane}
                          onNavigateToField={handleSetNavigateToField}
                          onAddTakeaway={handleAddTakeaway}
                          saveProgressRef={saveProgressRef}
                          hasChangesRef={hasChangesRef}
                          activityMetadata={activityMetadata}
                          currentActivityRef={currentActivityRef}
                          setCurrentActivityForExample={
                            setCurrentActivityForExample
                          }
                          onFollowExample={handleFollowExample}
                        />
                      </div>
                    </Panel>

                    <PanelResizeHandle className="w-2 transition-all duration-200 bg-purple-300 hover:bg-purple-500 hover:w-2.5 shadow-md" />

                    {/* Example Pane */}
                    <Panel defaultSize={50} minSize={35}>
                      {currentActivityRef.current && (
                        <ExampleFollowerPane
                          key={`example-${currentActivityForExample}`}
                          diagram={currentActivityRef.current.diagram}
                          currentActivity={
                            currentActivityForExample ||
                            currentActivityRef.current.currentActivity
                          }
                          exampleLogEntries={selectedExample.logEntries}
                          exampleNotes={selectedExample.notes}
                          exampleName={selectedExample.name}
                          creatorName={selectedExample.creatorName}
                          onClose={handleStopFollowingExample}
                          processId={processId}
                          prescriptionLanguage={
                            currentActivityRef.current.prescriptionLanguage ||
                            "en"
                          }
                          prologueRecordings={{
                            ...(selectedExample.prologueRecordings || {}),
                            image: selectedExample.prologueImage,
                          }}
                          epilogueRecordings={{
                            ...(selectedExample.epilogueRecordings || {}),
                            image: selectedExample.epilogueImage,
                          }}
                        />
                      )}
                    </Panel>
                  </PanelGroup>
                ) : (
                  // Normal single view
                  <div className="h-full overflow-auto bg-gradient-to-b from-slate-50/60 via-white to-indigo-50/20">
                    <ActivityRunningPreview
                      setActivityLastUpdatedDate={setActivityLastUpdatedDate}
                      setPrescriptionVersionNo={setPrescriptionVersionNo}
                      setIsActivityCompletedModalOpen={
                        setIsActivityCompletedModalOpen
                      }
                      processId={processId}
                      setProcessId={setProcessId}
                      onQuotePane={handleQuotePane}
                      onNavigateToField={handleSetNavigateToField}
                      onAddTakeaway={handleAddTakeaway}
                      saveProgressRef={saveProgressRef}
                      hasChangesRef={hasChangesRef}
                      activityMetadata={activityMetadata}
                      currentActivityRef={currentActivityRef}
                      setCurrentActivityForExample={
                        setCurrentActivityForExample
                      }
                      onFollowExample={handleFollowExample}
                    />
                  </div>
                )}
              </Panel>

              {/* Chat Panel - Right Side, Collapsible */}
              {!isChatCollapsed && (
                <>
                  <PanelResizeHandle className="w-1 transition-all duration-200 bg-border hover:bg-primary/50 hover:w-1.5 shadow-sm" />
                  <Panel
                    defaultSize={25}
                    minSize={15}
                    maxSize={40}
                    collapsible={true}
                    onCollapse={() => setIsChatCollapsed(true)}
                    onExpand={() => setIsChatCollapsed(false)}
                  >
                    <div className="h-full border-l bg-background">
                      <ProcessChat
                        processId={processId}
                        isMobile={false}
                        paneReference={paneReference}
                        onPaneReferenceUsed={() => {
                          setPaneReference(null);
                        }}
                        onPaneReferenceClick={handleNavigateToPane}
                        onCollapse={() => setIsChatCollapsed(true)}
                      />
                    </div>
                  </Panel>
                </>
              )}
            </PanelGroup>
          ))}

        {/* Takeaways Indicator Tab - Desktop only, when takeaways is collapsed */}
        {!isMobile && isTakeawaysCollapsed && (
          <TakeawaysIndicatorTab
            onClick={() => setIsTakeawaysCollapsed(false)}
          />
        )}

        {/* Chat Indicator Tab - Desktop only, when chat is collapsed */}
        {!isMobile && isChatCollapsed && (
          <ChatIndicatorTab onClick={() => setIsChatCollapsed(false)} />
        )}

        {/* <ExamplesDialog
        showExamplesDialog={showExamplesDialog}
        setShowExamplesDialog={setShowExamplesDialog}
        lastViewedExample={lastViewedExample}
        setLastViewedExample={setLastViewedExample}
      /> */}
        {showWorkflowInfoDialog && (
          <WorkflowInfoDialog
            showWorkflowInfoDialog={showWorkflowInfoDialog}
            setShowWorkflowInfoDialog={setShowWorkflowInfoDialog}
            activityLastUpdatedDate={activityLastUpdatedDate}
            prescriptionVersionNo={prescriptionVersionNo}
            activityMetadata={activityMetadata}
            userInfo={userInfo}
          />
        )}
        {isSaveAndExitModalOpen && (
          <SaveAndContinueModal
            isDialogOpen={isSaveAndExitModalOpen}
            setIsDialogOpen={(open: boolean) => {
              // Only close the modal, don't navigate
              if (!open) {
                setIsSaveAndExitModalOpen(false);
              }
            }}
            onAfterSaveOrDiscard={() => {
              // Navigate after save/discard
              const destinationAction =
                getDestinationAction(saveAndExitDestination) ??
                navigateToPortal;
              destinationAction();
            }}
            processId={processId}
            prescriptionId={prescriptionId}
            isUserSignedIn={!!auth?.email}
            activityTitle={
              activityMetadata?.name || engagementName || "this activity"
            }
            onSave={async () => {
              saveChanges.current = true;
              // Save current progress before exiting
              if (saveProgressRef.current) {
                await saveProgressRef.current();
              }
            }}
            onDiscard={() => {
              saveChanges.current = false;
            }}
          />
        )}
        {isActivityCompletedModalOpen && (
          <ActivityCompletionModal
            isDialogOpen={isActivityCompletedModalOpen}
            setIsDialogOpen={setIsActivityCompletedModalOpen}
            isUserSignedIn={!!auth?.email}
            processId={processId}
            forwardLocation={forward}
            activityTitle={
              activityMetadata?.name || engagementName || "this activity"
            }
          />
        )}
        {isReminderManagementOpen && (
          <ReminderManagementModal
            isOpen={isReminderManagementOpen}
            onClose={() => setIsReminderManagementOpen(false)}
            processId={processId}
            activityTitle={
              activityMetadata?.name || engagementName || "this activity"
            }
          />
        )}

        {/* Floating Visiting Card - Shows on first visit */}
        {showWelcomeScreen && activityMetadata && (
          <ActivityVisitingCard
            activityMetadata={activityMetadata}
            onStartActivity={handleStartActivity}
            onDismiss={handleSkipWelcome}
            position={cardPosition}
          />
        )}

        {/* Activity Usage Guide Modal */}
        <ActivityUsageGuide
          isOpen={showUsageGuide}
          onClose={() => setShowUsageGuide(false)}
          activityName={activityMetadata?.name || "this activity"}
          isAuthenticated={isAuthenticated}
        />
      </div>
    </DndContext>
  );
};

export default RunActivityPage;
